import React, {
  useCallback, useEffect, useState, useMemo, ReactElement,
} from 'react';
import Tooltip from '../../../components/tooltip/tooltip.component';
import TableRowForm from '../../../components/table-row-form/table-row-form.component';
import TableCell from '../../../components/table-cell/table-cell.component';
import FilteredCell from '../../../components/filtered-cell/filtered-cell.component';
import Loader from '../../../components/loader/loader.component';
import ActionButton from '../../../components/action-button/action-button.component';
import IconBlockError from '../../../components/icon-block-error/icon-block-error.component';
import IconBlockSuccess from '../../../components/icon-block-success/icon-block-success.component';
import VisibleCell from '../../../components/visible-cell/visible-cell.component';
import FormFieldSelect from '../../../components/form-field-select/form-field-select.component';
import FormFieldMultiSelect from '../../../components/form-field-multi-select/form-field-multi-select.component';
import FormFieldText from '../../../components/form-field-text/form-field-text.component';
import FormFieldNumber from '../../../components/form-field-number/form-field-number.component';
import FieldSeparatedBlock from '../../../components/field-separated-block/field-separated-block.component';
import IconTextBlock from '../../../components/icon-text-block/icon-text-block.component';
import MenuItemList from '../../../components/menu-item-list/menu-item-list.component';
import StructureService from '../../../services/structure-service';
import FormService from '../../../services/form-service';
import useShiftState from '../../../hooks/use-shift-state';
import { ACTIVITY_CATEGORY_NEW_DEAL } from '../../../constants/entity/activity-categories';
import PersonDefaultImage from '../../../static/images/person-default-image.png';
import CompanyDefaultImage from '../../../static/images/company-default-image.png';
import AssetDefaultImage from '../../../static/images/asset-default-image.png';

const ActivityRowForm: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isActive,
    isSaving,
    isSaveDisabled,
    isInlineSaving,
    isDealActivity,
    currentUser,
    tableName,
    dealId,
    activityData,
    activityIndex,
    tableColumnSettings,
    inputRefs,
    category,
    activityCategoryList,
    activityCategoryIconMap,
    activityStatusList,
    contactList,
    contactCompanyList,
    contactPersonList,
    contactPersonInternalList,
    measureUnitList,
    activityInterestLevelList,
    activityTypeList,
    dealList,
    tagList,
    onInlineSave,
    onInlineCancel,
    onTagsChange,
    onDealContactsChange,
    onActivityDealSearch,
    onActivityDealSearchClose,
    onContactSearch,
    onContactSearchClose,
    onContactPersonSearch,
    onContactPersonSearchClose,
    onContactPersonInternalSearch,
    onContactPersonInternalSearchClose,
    onContactCompanySearch,
    onContactCompanySearchClose,
    onTagSearch,
    onTagSearchClose,
  } = props;

  const isShiftPressed: boolean = useShiftState();

  const [selectedTags, setSelectedTags]: [Tag | null, Function] = useState(null);
  const [selectedDealContacts, setSelectedDealContacts]: [Contact | null, Function] = useState(null);

  const activityId: number = activityData.id;
  const formKeyActivity: string = FormService.getIndexedFormFieldKey(activityData.id);
  const formPrefix: string = isDealActivity ? `dealData.activityListData[${formKeyActivity}]`
    : `activityListData[${formKeyActivity}]`;
  const activityCategory: string | null = (activityData && activityData.category) || null;
  const isNewDealCategory: boolean = activityCategory === ACTIVITY_CATEGORY_NEW_DEAL;

  const menuItemActivityStatusList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: activityStatusList,
  }), [activityStatusList]);

  const menuItemActivityTypeList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: activityTypeList,
  }), [activityTypeList]);

  const menuItemDealList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withImage: true,
    isExtended: true,
    optionItemLabelKey: 'name',
    optionList: dealList,
    optionItemImageKey: 'mainPhoto',
    optionItemImageThumbnailKey: 'mainPhotoThumbnail',
    defaultImage: AssetDefaultImage,
  }), [dealList]);

  const menuItemContactCompanyList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withImage: true,
    isExtended: true,
    optionItemLabelKey: 'fullName',
    optionList: contactCompanyList,
    defaultImage: CompanyDefaultImage,
  }), [contactCompanyList]);

  const menuItemContactDealContactList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withImage: true,
    isExtended: true,
    optionItemLabelKey: 'fullName',
    optionList: isNewDealCategory ? contactPersonInternalList : contactPersonList,
    defaultImage: PersonDefaultImage,
    selectedItemList: selectedDealContacts,
  }), [contactPersonInternalList, contactPersonList, isNewDealCategory, selectedDealContacts]);

  const menuItemMeasureUnitList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: measureUnitList,
  }), [measureUnitList]);

  const menuItemActivityInterestLevelList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: activityInterestLevelList,
  }), [activityInterestLevelList]);

  const menuItemContactList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withImage: true,
    isExtended: true,
    optionItemLabelKey: 'fullName',
    optionList: contactList,
    defaultImage: CompanyDefaultImage,
  }), [contactList]);

  const menuItemTagList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionItemLabelKey: 'name',
    optionList: tagList,
    selectedItemList: selectedTags,
  }), [tagList, selectedTags]);

  const tagsChange: Function = useCallback((tagValues: Tag[]): void => {
    if (onTagsChange) {
      onTagsChange(activityId, tagValues);
    }

    setSelectedTags(tagValues);
  }, [activityId]);

  const dealContactsChange: Function = useCallback((contactValues: Contact): void => {
    if (onDealContactsChange) {
      onDealContactsChange(activityId, contactValues);
    }

    setSelectedDealContacts(contactValues);
  }, [activityId]);

  const tableActivityListFields: TableField[] = useMemo((): TableField[] => [
    {
      id: 1,
      name: 'category',
      isHidden: !!category,
      component: (): ReactElement | boolean => {
        const activityCategory: ActivityCategory | null = activityCategoryList && activityCategoryList.length
          ? activityCategoryList.find((activityCategoryItem: ActivityCategory): boolean => (
            activityCategoryItem.name === activityData.category
          )) : null;
        const activityCategoryIcon: ReactElement | undefined = (activityCategory && activityCategoryIconMap
          && activityCategoryIconMap[activityCategory.name]) || undefined;

        return !!activityCategory && (
          <TableCell isDisabled>
            <IconTextBlock
              icon={(
                !!activityCategoryIcon && (
                  <Tooltip title={activityCategory.title}>
                    {activityCategoryIcon}
                  </Tooltip>
                )
              )}
            >
              {activityCategory.title}
            </IconTextBlock>
          </TableCell>
        );
      },
    },
    {
      id: 2,
      name: 'status',
      component: (tableField: TableField, tableFieldIndex: number): ReactElement => {
        const isDisabled: boolean = activityStatusList && !activityStatusList.length;

        return (
          <TableCell>
            <FormFieldSelect
              withBlankElement
              isDisabled={isDisabled}
              isReadOnly={isShiftPressed}
              name={`${formPrefix}.status`}
              optionsTotal={activityStatusList && activityStatusList.length}
              inputRefs={inputRefs}
              inputRefIndex={!isDisabled ? `${activityIndex}.${tableFieldIndex}` : null}
            >
              {menuItemActivityStatusList}
            </FormFieldSelect>
          </TableCell>
        );
      },
    },
    {
      id: 3,
      name: 'type',
      component: (tableField: TableField, tableFieldIndex: number): ReactElement => {
        const isDisabled: boolean = activityTypeList && !activityTypeList.length;

        return (
          <TableCell>
            <FormFieldSelect
              isDisabled={isDisabled}
              isReadOnly={isShiftPressed}
              name={`${formPrefix}.type`}
              optionsTotal={activityTypeList && activityTypeList.length}
              inputRefs={inputRefs}
              inputRefIndex={!isDisabled ? `${activityIndex}.${tableFieldIndex}` : null}
            >
              {menuItemActivityTypeList}
            </FormFieldSelect>
          </TableCell>
        );
      },
    },
    {
      id: 4,
      name: 'deal.name',
      component: (tableField: TableField, tableFieldIndex: number): ReactElement | boolean => (
        !dealId && (
          <TableCell isSticky isStickyLeft>
            {
              !dealId && (
                <FormFieldSelect
                  withSearch
                  withBlankElement
                  isDisabled={dealList && !dealList.length}
                  isReadOnly={isShiftPressed}
                  name={`${formPrefix}.dealId`}
                  optionsTotal={dealList && dealList.length}
                  inputRefs={inputRefs}
                  inputRefIndex={`${activityIndex}.${tableFieldIndex}`}
                  onSearch={onActivityDealSearch}
                  onClose={onActivityDealSearchClose}
                >
                  {menuItemDealList}
                </FormFieldSelect>
              )
            }
          </TableCell>
        )
      ),
    },
    {
      id: 5,
      name: 'dealSource.name',
      component: (tableField: TableField, tableFieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldSelect
            withSearch
            withBlankElement
            isDisabled={contactCompanyList && !contactCompanyList.length}
            isReadOnly={isShiftPressed}
            name={`${formPrefix}.dealSourceId`}
            optionsTotal={contactCompanyList && contactCompanyList.length}
            inputRefs={inputRefs}
            inputRefIndex={`${activityIndex}.${tableFieldIndex}`}
            onSearch={onContactCompanySearch}
            onClose={onContactCompanySearchClose}
          >
            {menuItemContactCompanyList}
          </FormFieldSelect>
        </TableCell>
      ),
    },
    {
      id: 6,
      name: 'sponsor.fullName',
      component: (tableField: TableField, tableFieldIndex: number): ReactElement => {
        const isDisabled: boolean = contactCompanyList && !contactCompanyList.length;

        return (
          <TableCell>
            <FormFieldSelect
              withSearch
              withBlankElement
              isDisabled={isDisabled}
              isReadOnly={isShiftPressed}
              name={`${formPrefix}.sponsorId`}
              optionsTotal={contactCompanyList && contactCompanyList.length}
              inputRefs={inputRefs}
              inputRefIndex={!isDisabled ? `${activityIndex}.${tableFieldIndex}` : null}
              onSearch={onContactCompanySearch}
              onClose={onContactCompanySearchClose}
            >
              {menuItemContactCompanyList}
            </FormFieldSelect>
          </TableCell>
        );
      },
    },
    {
      id: 7,
      name: 'broker.fullName',
      component: (tableField: TableField, tableFieldIndex: number): ReactElement => {
        const isDisabled: boolean = contactCompanyList && !contactCompanyList.length;

        return (
          <TableCell>
            <FormFieldSelect
              withSearch
              withBlankElement
              isDisabled={isDisabled}
              isReadOnly={isShiftPressed}
              name={`${formPrefix}.brokerId`}
              optionsTotal={contactCompanyList && contactCompanyList.length}
              inputRefs={inputRefs}
              inputRefIndex={!isDisabled ? `${activityIndex}.${tableFieldIndex}` : null}
              onSearch={onContactCompanySearch}
              onClose={onContactCompanySearchClose}
            >
              {menuItemContactCompanyList}
            </FormFieldSelect>
          </TableCell>
        );
      },
    },
    {
      id: 8,
      name: 'lender.fullName',
      component: (tableField: TableField, tableFieldIndex: number): ReactElement => {
        const isDisabled: boolean = contactCompanyList && !contactCompanyList.length;

        return (
          <TableCell>
            <FormFieldSelect
              withSearch
              withBlankElement
              isDisabled={isDisabled}
              isReadOnly={isShiftPressed}
              name={`${formPrefix}.lenderId`}
              optionsTotal={contactCompanyList && contactCompanyList.length}
              inputRefs={inputRefs}
              inputRefIndex={!isDisabled ? `${activityIndex}.${tableFieldIndex}` : null}
              onSearch={onContactCompanySearch}
              onClose={onContactCompanySearchClose}
            >
              {menuItemContactCompanyList}
            </FormFieldSelect>
          </TableCell>
        );
      },
    },
    {
      id: 9,
      name: 'amount',
      component: (tableField: TableField, tableFieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldNumber
            name={`${formPrefix}.amount`}
            inputRefs={inputRefs}
            inputRefIndex={`${activityIndex}.${tableFieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 10,
      name: 'pricing',
      component: (tableField: TableField, tableFieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldText
            name={`${formPrefix}.pricing`}
            inputRefs={inputRefs}
            inputRefIndex={`${activityIndex}.${tableFieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 11,
      name: 'size',
      component: (tableField: TableField, tableFieldIndex: number): ReactElement => {
        const isDisabled: boolean = measureUnitList && !measureUnitList.length;
        const subFieldIndexSize: number = 1;
        const subFieldIndexSizeType: number = 2;

        return (
          <TableCell>
            <FieldSeparatedBlock>
              <FormFieldNumber
                isDisabled={isDisabled}
                name={`${formPrefix}.size`}
                inputRefs={inputRefs}
                inputRefIndex={`${activityIndex}.${tableFieldIndex}.${subFieldIndexSize}`}
              />
              <FormFieldSelect
                withBlankElement
                isDisabled={isDisabled}
                isReadOnly={isShiftPressed}
                name={`${formPrefix}.sizeType`}
                optionsTotal={measureUnitList && measureUnitList.length}
                inputRefs={inputRefs}
                inputRefIndex={!isDisabled ? `${activityIndex}.${tableFieldIndex}.${subFieldIndexSizeType}` : null}
              >
                {menuItemMeasureUnitList}
              </FormFieldSelect>
            </FieldSeparatedBlock>
          </TableCell>
        );
      },
    },
    {
      id: 12,
      name: 'dealContacts.fullName',
      component: (tableField: TableField, tableFieldIndex: number): ReactElement => {
        const isDisabled: boolean = isNewDealCategory ? true : (contactPersonList && !contactPersonList.length);

        return (
          <TableCell>
            <FormFieldMultiSelect
              withImage
              withSearch
              isReadOnly={isShiftPressed}
              isDisabled={isDisabled}
              nameKey="fullName"
              name={`${formPrefix}.dealContactIds`}
              inputRefs={inputRefs}
              inputRefIndex={!isDisabled ? `${activityIndex}.${tableFieldIndex}` : null}
              defaultImage={PersonDefaultImage}
              optionList={isNewDealCategory ? contactPersonInternalList : contactPersonList}
              onValuesChange={dealContactsChange}
              onSearch={isNewDealCategory ? onContactPersonInternalSearch : onContactPersonSearch}
              onClose={isNewDealCategory ? onContactPersonInternalSearchClose : onContactPersonSearchClose}
            >
              {menuItemContactDealContactList}
            </FormFieldMultiSelect>
          </TableCell>
        );
      },
    },
    {
      id: 13,
      name: 'dealContacts.phone',
      component: (): ReactElement => (
        <TableCell isText isDisabled>
          {
            activityData.dealContacts && !!activityData.dealContacts.length
            && activityData.dealContacts.map((contactItem: Contact): ReactElement => (
              <div key={contactItem.id}>
                {contactItem.phoneText}
              </div>
            ))
          }
        </TableCell>
      ),
    },
    {
      id: 14,
      name: 'dealContacts.email',
      component: (): ReactElement => (
        <TableCell isText isDisabled>
          {
            activityData.dealContacts && !!activityData.dealContacts.length
            && activityData.dealContacts.map((contactItem: Contact): ReactElement => (
              <div key={contactItem.id}>
                {contactItem.email}
              </div>
            ))
          }
        </TableCell>
      ),
    },
    {
      id: 15,
      name: 'purchasePrice',
      component: (tableField: TableField, tableFieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldNumber
            name={`${formPrefix}.purchasePrice`}
            inputRefs={inputRefs}
            inputRefIndex={`${activityIndex}.${tableFieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 16,
      name: 'capitalExpenditure',
      component: (tableField: TableField, tableFieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldNumber
            name={`${formPrefix}.capitalExpenditure`}
            inputRefs={inputRefs}
            inputRefIndex={`${activityIndex}.${tableFieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 17,
      name: 'oneYearNoi',
      component: (tableField: TableField, tableFieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldNumber
            name={`${formPrefix}.oneYearNoi`}
            inputRefs={inputRefs}
            inputRefIndex={`${activityIndex}.${tableFieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 18,
      name: 'yieldOnCost',
      component: (tableField: TableField, tableFieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldNumber
            isPercent
            isFloat
            withNoLimit
            name={`${formPrefix}.yieldOnCost`}
            inputRefs={inputRefs}
            inputRefIndex={`${activityIndex}.${tableFieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 19,
      name: 'debtProceeds',
      component: (tableField: TableField, tableFieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldNumber
            name={`${formPrefix}.debtProceeds`}
            inputRefs={inputRefs}
            inputRefIndex={`${activityIndex}.${tableFieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 20,
      name: 'requiredEquityAmount',
      component: (tableField: TableField, tableFieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldNumber
            name={`${formPrefix}.requiredEquityAmount`}
            inputRefs={inputRefs}
            inputRefIndex={`${activityIndex}.${tableFieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 21,
      name: 'interestLevel',
      component: (tableField: TableField, tableFieldIndex: number): ReactElement => {
        const isDisabled: boolean = activityInterestLevelList && !activityInterestLevelList.length;

        return (
          <TableCell>
            <FormFieldSelect
              withBlankElement
              isDisabled={isDisabled}
              isReadOnly={isShiftPressed}
              name={`${formPrefix}.interestLevel`}
              optionsTotal={activityInterestLevelList && activityInterestLevelList.length}
              inputRefs={inputRefs}
              inputRefIndex={!isDisabled ? `${activityIndex}.${tableFieldIndex}` : null}
            >
              {menuItemActivityInterestLevelList}
            </FormFieldSelect>
          </TableCell>
        );
      },
    },
    {
      id: 22,
      name: 'ltc',
      component: (tableField: TableField, tableFieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldNumber
            isPercent
            isFloat
            withNoLimit
            name={`${formPrefix}.ltc`}
            inputRefs={inputRefs}
            inputRefIndex={`${activityIndex}.${tableFieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 23,
      name: 'equityProvider.fullName',
      component: (tableField: TableField, tableFieldIndex: number): ReactElement => {
        const isDisabled: boolean = contactList && !contactList.length;

        return (
          <TableCell>
            <FormFieldSelect
              withSearch
              withBlankElement
              isDisabled={isDisabled}
              isReadOnly={isShiftPressed}
              name={`${formPrefix}.equityProviderId`}
              optionsTotal={contactList && contactList.length}
              inputRefs={inputRefs}
              inputRefIndex={!isDisabled ? `${activityIndex}.${tableFieldIndex}` : null}
              onSearch={onContactSearch}
              onClose={onContactSearchClose}
            >
              {menuItemContactList}
            </FormFieldSelect>
          </TableCell>
        );
      },
    },
    {
      id: 24,
      name: 'investor.fullName',
      component: (tableField: TableField, tableFieldIndex: number): ReactElement => {
        const isDisabled: boolean = contactList && !contactList.length;

        return (
          <TableCell>
            <FormFieldSelect
              withSearch
              withBlankElement
              isDisabled={isDisabled}
              isReadOnly={isShiftPressed}
              name={`${formPrefix}.investorId`}
              optionsTotal={contactList && contactList.length}
              inputRefs={inputRefs}
              inputRefIndex={!isDisabled ? `${activityIndex}.${tableFieldIndex}` : null}
              onSearch={onContactSearch}
              onClose={onContactSearchClose}
            >
              {menuItemContactList}
            </FormFieldSelect>
          </TableCell>
        );
      },
    },
    {
      id: 25,
      name: 'lenderRole',
      component: (tableField: TableField, tableFieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldText
            name={`${formPrefix}.lenderRole`}
            inputRefs={inputRefs}
            inputRefIndex={`${activityIndex}.${tableFieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 26,
      name: 'maxAmount',
      component: (tableField: TableField, tableFieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldText
            name={`${formPrefix}.maxAmount`}
            inputRefs={inputRefs}
            inputRefIndex={`${activityIndex}.${tableFieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 27,
      name: 'loanRecourse',
      component: (tableField: TableField, tableFieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldText
            name={`${formPrefix}.loanRecourse`}
            inputRefs={inputRefs}
            inputRefIndex={`${activityIndex}.${tableFieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 28,
      name: 'additionalParty.fullName',
      component: (tableField: TableField, tableFieldIndex: number): ReactElement => {
        const isDisabled: boolean = contactList && !contactList.length;
        const tableFieldIndexExtra: number = 2;

        return (
          <TableCell>
            <FieldSeparatedBlock>
              <FormFieldSelect
                withSearch
                withBlankElement
                isDisabled={isDisabled}
                isReadOnly={isShiftPressed}
                name={`${formPrefix}.additionalPartyId`}
                optionsTotal={contactList && contactList.length}
                inputRefs={inputRefs}
                inputRefIndex={!isDisabled ? `${activityIndex}.${tableFieldIndex}` : null}
                onSearch={onContactSearch}
                onClose={onContactSearchClose}
              >
                {menuItemContactList}
              </FormFieldSelect>
              <FormFieldText
                isDisabled={isDisabled}
                name={`${formPrefix}.additionalPartyDetails`}
                placeholder="Details"
                inputRefs={inputRefs}
                inputRefIndex={`${activityIndex}.${tableFieldIndex}.${tableFieldIndexExtra}`}
              />
            </FieldSeparatedBlock>
          </TableCell>
        );
      },
    },
    {
      id: 29,
      name: 'updatedDate',
      component: (): ReactElement => (
        <TableCell />
      ),
    },
    {
      id: 30,
      name: 'tags',
      component: (tableField: TableField, tableFieldIndex: number): ReactElement => {
        const isDisabled: boolean = tagList && !tagList.length;

        return (
          <TableCell>
            <FormFieldMultiSelect
              isAbleToAdd
              withSearch
              isDisabled={isDisabled}
              isReadOnly={isShiftPressed}
              keyField="name"
              name={`${formPrefix}.tags`}
              optionList={tagList}
              inputRefs={inputRefs}
              inputRefIndex={!isDisabled ? `${activityIndex}.${tableFieldIndex}` : null}
              onSearch={onTagSearch}
              onClose={onTagSearchClose}
              onValuesChange={tagsChange}
            >
              {menuItemTagList}
            </FormFieldMultiSelect>
          </TableCell>
        );
      },
    },
    {
      id: 31,
      name: 'actions',
      isNotFiltered: true,
      component: (): ReactElement => (
        <TableCell isActions isTextRight isSticky isStickyRight>
          {
            isSaving && isInlineSaving && (
              <Loader isInline isSmall />
            )
          }
          {
            !isSaveDisabled && !isSaving && (
              <ActionButton
                withSmallPadding
                onClick={(): void => {
                  onInlineSave(activityId);
                }}
              >
                <IconBlockSuccess />
              </ActionButton>
            )
          }
          {
            !isSaving && (
              <ActionButton
                withSmallPadding
                onClick={(): void => {
                  onInlineCancel(activityId);
                }}
              >
                <IconBlockError isDisabled={isSaveDisabled} />
              </ActionButton>
            )
          }
        </TableCell>
      ),
    },
  ], [
    isShiftPressed, selectedDealContacts, selectedTags, isSaving, isSaveDisabled, category, activityData,
    contactList, contactPersonList, contactCompanyList, contactPersonInternalList, dealList, tagList, activityId,
  ]);

  useEffect((): void => {
    if (activityData) {
      if (!selectedTags) {
        setSelectedTags(activityData.tags || []);
      }

      if (!selectedDealContacts) {
        setSelectedDealContacts(activityData.dealContacts && activityData.dealContacts.length
          ? activityData.dealContacts : []);
      }
    }
  }, [activityData]);

  return (
    <TableRowForm isActive={isActive}>
      {
        tableActivityListFields && !!tableActivityListFields.length
        && StructureService.extendOptions(tableActivityListFields, tableColumnSettings, true).map((
          tableField: TableField, tableFieldIndex: number,
        ): ReactElement => (
          <VisibleCell withEntity={!dealId} key={tableField.id} tableField={tableField}>
            {
              !!tableField.component && (
                !tableField.isNotFiltered ? (
                  <FilteredCell
                    tableName={tableName}
                    columnName={tableField.name}
                    currentUser={currentUser}
                  >
                    {tableField.component(tableField, tableFieldIndex)}
                  </FilteredCell>
                ) : (
                  tableField.component(tableField, tableFieldIndex)
                )
              )
            }
          </VisibleCell>
        ))
      }
    </TableRowForm>
  );
});

export default ActivityRowForm;
